import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, PointerEvent } from 'react';

import type { Map as LeafletMapInstance } from 'leaflet';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { notification } from 'antd';

import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import { useIsMobile } from '@src/packages/utils';
import { ALLOWED_ROLES, hasAnyAllowedRole, resolveOrgAccess } from '@src/packages/utils/access';

import {
  buildActiveUserStalledFilterSetValueProps,
  buildActiveUserLowBatteryFilterSetValueProps,
  buildActiveUserStatusFilterSetValueProps,
  INIT_FILTERS,
} from '../activeUserFilterConfig';
import {
  DEFAULT_SORT_STATE,
  USER_LIST_HEADER_HEIGHT,
  USER_LIST_MOBILE_HEADER_HEIGHT,
  USER_LIST_MOBILE_ROW_HEIGHT,
  USER_LIST_MOBILE_SEARCH_HEIGHT,
  USER_LIST_DESKTOP_RESERVED_HEIGHT,
  USER_LIST_ROW_HEIGHT,
} from '../constants';
import type {
  ActiveUserDashboardHookReturn,
  ActiveUserDashboardReduxState,
  ActiveUserExecutionDetail,
  ActiveUserListItem,
  ExecutionComplianceType,
  SortState,
  UserLocationTrackerRef,
} from '../types';
import {
  buildUserList,
  computeKpis,
  getConfiguredTimeZone,
  getFilteredUsers,
  getUserListPageSize,
  shouldShowMobileSearch,
  sortUsers,
} from '../utils';
import { useActiveUsersRoster } from './useActiveUsersRoster';
import { useNowTicker } from './useNowTicker';
import { useExecutionCompliance } from './useExecutionCompliance';
import { useLiveWatchLease } from './useLiveWatchLease';
import { useUsersLiveStatus } from './useUsersLiveStatus';

export const useActiveUserDashboard = (): ActiveUserDashboardHookReturn => {
  const intl = useIntl();
  const { isMobile } = useIsMobile();
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const mapRef = useRef<LeafletMapInstance | null>(null);
  const userTrackerRef = useRef<UserLocationTrackerRef | null>(null);
  const initialFiltersRef = useRef(INIT_FILTERS);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const listOverlayRef = useRef<HTMLDivElement>(null);

  const {
    values: filters,
    setValue: setFilters,
    toggle,
    setValues,
    isOpen,
    close: closeFilterPanel,
  } = useSecondaryPanel();

  const [userFocusedId, setUserFocusedId] = useState<string | null>(null);
  const [focusRequestId, setFocusRequestId] = useState(0);
  const [fitAllRequestId, setFitAllRequestId] = useState(0);
  const [sortState, setSortState] = useState<SortState>(DEFAULT_SORT_STATE);
  const [mobileListExpanded, setMobileListExpanded] = useState(false);
  const [mobileUserSearch, setMobileUserSearch] = useState('');
  const [listHeight, setListHeight] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [complianceDrawer, setComplianceDrawer] = useState<{
    user: ActiveUserListItem;
    type: ExecutionComplianceType;
  } | null>(null);
  const isOverviewRoute = useRouteMatch({ path: url, exact: true }) !== null;
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const returnedToOverview = isOverviewRoute && previousPathRef.current !== location.pathname;
    if (returnedToOverview) {
      setUserFocusedId(null);
      setFitAllRequestId(requestId => requestId + 1);
      mapRef.current?.closePopup();
    }
    previousPathRef.current = location.pathname;
  }, [isOverviewRoute, location.pathname]);
  const toggleListCollapsed = useCallback(() => setListCollapsed(value => !value), []);
  const closeUserProfile = useCallback(() => setProfileUserId(null), []);
  const handleMobileUserSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setMobileUserSearch(event.target.value);
    setListPage(1);
  }, []);
  const sheetPointerStartRef = useRef<number | null>(null);
  const sheetDraggedRef = useRef(false);
  const toggleMobileList = useCallback(() => {
    if (sheetDraggedRef.current) {
      sheetDraggedRef.current = false;
      return;
    }
    setMobileListExpanded(expanded => !expanded);
  }, []);
  const handleSheetPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0) return;
    sheetDraggedRef.current = false;
    sheetPointerStartRef.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);
  const handleSheetPointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const sheet = listOverlayRef.current;
    if (!sheet || sheetPointerStartRef.current === null) return;
    const delta = event.clientY - sheetPointerStartRef.current;
    if (Math.abs(delta) < 6) return;
    const travel = Math.max(0, sheet.clientHeight - USER_LIST_MOBILE_HEADER_HEIGHT);
    const offset = mobileListExpanded
      ? Math.max(0, Math.min(travel, delta))
      : Math.max(-travel, Math.min(0, delta));
    sheet.dataset.dragging = 'true';
    sheet.style.setProperty('--users-sheet-drag', `${offset}px`);
  }, [mobileListExpanded]);
  const handleSheetPointerUp = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (sheetPointerStartRef.current === null) return;
    const delta = event.clientY - sheetPointerStartRef.current;
    sheetPointerStartRef.current = null;
    const sheet = listOverlayRef.current;
    if (sheet) {
      delete sheet.dataset.dragging;
      sheet.style.removeProperty('--users-sheet-drag');
    }
    if (Math.abs(delta) < 24) return;
    sheetDraggedRef.current = true;
    setMobileListExpanded(delta < 0);
  }, []);
  const handleSheetPointerCancel = useCallback(() => {
    sheetPointerStartRef.current = null;
    sheetDraggedRef.current = false;
    const sheet = listOverlayRef.current;
    if (sheet) {
      delete sheet.dataset.dragging;
      sheet.style.removeProperty('--users-sheet-drag');
    }
  }, []);

  const orgId = useSelector(
    (state: ActiveUserDashboardReduxState) => state.organizations.organization.id,
  );
  const selectedDivsIds = useSelector(
    ({ divisions }: ActiveUserDashboardReduxState) => divisions.selector.ids || [],
  );
  const currentUser = useSelector(
    ({ user }: ActiveUserDashboardReduxState) => user.profile?.data,
  );
  const currentUserPicture = useSelector(
    ({ user }: ActiveUserDashboardReduxState) => user.access.data?.claims.picture,
  );
  const claims = useSelector(({ user }: ActiveUserDashboardReduxState) => user.access.data?.claims);
  const orgUserAccess = useSelector(
    ({ user }: ActiveUserDashboardReduxState) => user.orgUser?.data?.access,
  );
  const assignedDivisions = useSelector(
    ({ divisions }: ActiveUserDashboardReduxState) => divisions.assigned.data.divisions,
  );
  const canViewUserProfile = hasAnyAllowedRole(
    ALLOWED_ROLES.ORGANIZATIONS.USERS.GET,
    resolveOrgAccess(claims, orgId, orgUserAccess),
    assignedDivisions,
  );
  const openUserProfile = useCallback((userId: string) => {
    if (canViewUserProfile) setProfileUserId(userId);
  }, [canViewUserProfile]);

  const { users, loading: rosterLoading, error: rosterError } = useActiveUsersRoster(orgId);
  const { statusByUser, loading: liveStatusLoading, error: liveStatusError } = useUsersLiveStatus(orgId);
  const { complianceByUser, detailsByUser, error: complianceError } = useExecutionCompliance(
    orgId,
    selectedDivsIds,
  );

  const nowMs = useNowTicker();
  const timeZone = useMemo(() => getConfiguredTimeZone(), []);

  // Only the roster blocks first paint; RTDB live status and compliance stream
  // in as they arrive so a slow/unavailable RTDB never locks the screen.
  const mainLoading = rosterLoading;
  const listLoading = rosterLoading || liveStatusLoading;
  const fitAllReady = !rosterLoading && !liveStatusLoading;
  const error = rosterError;
  const dataStreamError = liveStatusError ?? complianceError ?? null;

  const userList = useMemo(
    () =>
      buildUserList({
        users,
        statusByUser,
        complianceByUser,
        userFocusedId,
        currentUserId: currentUser?.id,
        currentUserPhotoURL: currentUserPicture || currentUser?.photoURL,
        nowMs,
        timeZone,
        intl,
      }),
    [users, statusByUser, complianceByUser, userFocusedId, currentUser?.id, currentUser?.photoURL,
      currentUserPicture, nowMs, timeZone, intl],
  );

  const filteredUsers = useMemo(
    () => getFilteredUsers(userList, filters, selectedDivsIds),
    [userList, filters, selectedDivsIds],
  );

  const recordsToShow = useMemo(
    () => sortUsers(filteredUsers, sortState),
    [filteredUsers, sortState],
  );

  const showMobileSearch = shouldShowMobileSearch(isMobile, userList.length);
  const mobileListUsers = useMemo(() => showMobileSearch
    ? getFilteredUsers(recordsToShow, {
      ...filters,
      text: { ...filters.text, value: mobileUserSearch },
    }, selectedDivsIds)
    : recordsToShow,
  [showMobileSearch, recordsToShow, filters, mobileUserSearch, selectedDivsIds]);
  const listUsers = isMobile ? mobileListUsers : recordsToShow;

  const listHeaderHeight = isMobile ? 0 : USER_LIST_HEADER_HEIGHT;
  const listRowHeight = isMobile ? USER_LIST_MOBILE_ROW_HEIGHT : USER_LIST_ROW_HEIGHT;
  const availableListHeight = Math.max(0, listHeight - (isMobile
    ? USER_LIST_MOBILE_HEADER_HEIGHT + (showMobileSearch ? USER_LIST_MOBILE_SEARCH_HEIGHT : 0)
    : USER_LIST_DESKTOP_RESERVED_HEIGHT));
  const listPageSize = getUserListPageSize(
    listUsers.length, availableListHeight, listHeaderHeight, listRowHeight,
  );
  const currentListPage = Math.min(
    listPage,
    Math.max(1, Math.ceil(listUsers.length / listPageSize)),
  );
  const handleListPageChange = useCallback((page: number) => setListPage(page), []);
  const divisionFilterKey = selectedDivsIds.join('|');

  useEffect(() => {
    setListPage(1);
  }, [filters, sortState, orgId, divisionFilterKey, listPageSize]);

  useEffect(() => {
    const container = isMobile ? listOverlayRef.current : listContainerRef.current;
    if (!container) return undefined;

    const updateHeight = () => setListHeight(container.clientHeight);
    updateHeight();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateHeight)
      : null;
    observer?.observe(container);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [mainLoading, error, isMobile]);

  const liveWatchTargets = useMemo(
    () =>
      recordsToShow.map(user => ({
        uid: user.id,
        divisionId: selectedDivsIds.find(divisionId =>
          Object.prototype.hasOwnProperty.call(user.divisions ?? {}, divisionId),
        ),
      })),
    [recordsToShow, selectedDivsIds],
  );

  useLiveWatchLease({
    enabled: Boolean(orgId),
    organizationId: orgId,
    targets: liveWatchTargets,
    viewerUid: currentUser?.id,
  });

  const kpis = useMemo(() => computeKpis(userList), [userList]);

  const userFocused = useMemo(
    () => (userFocusedId ? userList.find(user => user.id === userFocusedId) ?? null : null),
    [userFocusedId, userList],
  );

  const handleSchedulesByUser = useCallback(
    (user: ActiveUserListItem) => {
      closeFilterPanel();
      history.push({ pathname: `${url}/schedules/${user.id}` });
    },
    [closeFilterPanel, history, url],
  );

  const handleTimelineByUser = useCallback(
    (userId: string) => {
      closeFilterPanel();
      history.push({ pathname: `${url}/timeline/${userId}` });
    },
    [closeFilterPanel, history, url],
  );

  const handleFindUser = useCallback(
    (user: ActiveUserListItem) => {
      if (!user.id) return;
      if (!user.positions) {
        notification.warning({
          message: intl.formatMessage({
            id: user.connected
              ? 'activeUserDashboard.findUserUnavailableGps'
              : 'activeUserDashboard.findUserUnavailableOffline',
          }, { name: user.displayName }),
        });
        return;
      }
      setUserFocusedId(user.id);
      setFocusRequestId(requestId => requestId + 1);
      closeUserProfile();
      if (isMobile) {
        setMobileListExpanded(false);
      }
      userTrackerRef.current?.openUserPopup(user.id);
    },
    [isMobile, closeUserProfile, intl],
  );

  const handleMarkerClick = useCallback((userId: string) => {
    setUserFocusedId(userId);
    closeUserProfile();
    setMobileListExpanded(false);
  }, [closeUserProfile]);

  /** Closing a marker popup releases the selection so the map stops following that user. */
  const handleMarkerClose = useCallback((userId: string) => {
    setUserFocusedId(prev => (prev === userId ? null : prev));
  }, []);

  const handleKpiFilter = useCallback(
    (kpi: 'connected' | 'stalled' | 'lowBattery') => {
      setValues({
        ...filters,
        status: buildActiveUserStatusFilterSetValueProps(kpi === 'connected' ? true : null, intl),
        stalled: buildActiveUserStalledFilterSetValueProps(kpi === 'stalled', intl),
        lowBattery: buildActiveUserLowBatteryFilterSetValueProps(kpi === 'lowBattery', intl),
      });
    },
    [filters, setValues, intl],
  );

  const openComplianceDrawer = useCallback(
    (user: ActiveUserListItem, type: ExecutionComplianceType) => setComplianceDrawer({ user, type }),
    [],
  );
  const closeComplianceDrawer = useCallback(() => setComplianceDrawer(null), []);
  const complianceDetails: ActiveUserExecutionDetail[] = useMemo(() => {
    if (!complianceDrawer) return [];
    return (detailsByUser[complianceDrawer.user.id] ?? []).filter(
      execution => execution.type === complianceDrawer.type,
    );
  }, [complianceDrawer, detailsByUser]);

  useEffect(() => {
    setValues(INIT_FILTERS);
  }, [setValues]);

  return {
    url,
    orgId,
    listCollapsed,
    focusRequestId,
    fitAllRequestId,
    listLoading,
    fitAllReady,
    toggleListCollapsed,
    profileUserId,
    canViewUserProfile,
    openUserProfile,
    closeUserProfile,
    isMobile,
    error,
    dataStreamError,
    mainLoading,
    userFocused,
    recordsToShow,
    mobileListUsers,
    showMobileSearch,
    mobileUserSearch,
    handleMobileUserSearch,
    listContainerRef,
    listOverlayRef,
    listPagination: {
      current: currentListPage,
      pageSize: listPageSize,
      total: listUsers.length,
      onChange: handleListPageChange,
    },
    kpis,
    sortState,
    setSortState,
    mapRef,
    userTrackerRef,
    filters,
    setFilters,
    initialFilters: initialFiltersRef.current,
    toggle,
    isOpen,
    mobileListExpanded,
    toggleMobileList,
    handleSheetPointerDown,
    handleSheetPointerMove,
    handleSheetPointerUp,
    handleSheetPointerCancel,
    handleFindUser,
    handleTimelineByUser,
    handleSchedulesByUser,
    handleMarkerClick,
    handleMarkerClose,
    handleKpiFilter,
    complianceDrawer,
    complianceDetails,
    closeComplianceDrawer,
    openComplianceDrawer,
  };
};
